import { useState, useEffect, useCallback } from 'react';
import {
  collection, onSnapshot, query, where, doc,
  addDoc, updateDoc, serverTimestamp,
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage, addAuditLog } from '../../../firebase';
import { useAuth } from '../../../context/AuthContext';
import ProductModal from '../../../components/ProductModal';
import ToastNotification from '../../../components/ToastNotification';
import styles from './ProductsSection.module.css';


const CATEGORIES = ['All', 'Tea Leaves', 'Coffee', 'Syrups', 'Dairy', 'Boba Supplies', 'Flavored Powders', 'Cups & Straws', 'Stationery'];

function stockStatus(stock) {
  if (stock >= 20) return 'In Stock';
  if (stock >= 10) return 'Low Stock';
  return 'Critical';
}

function StatusBadge({ stock }) {
  const status = stockStatus(stock);
  const cls = {
    'In Stock':  styles.badgeGreen,
    'Low Stock': styles.badgeYellow,
    'Critical':  styles.badgeRed,
  }[status];
  return <span className={`${styles.badge} ${cls}`}>{status}</span>;
}

function ConfirmDialog({ productName, onConfirm, onCancel }) {
  return (
    <div className={styles.overlay} onClick={onCancel}>
      <div className={styles.confirmBox} onClick={e => e.stopPropagation()}>
        <h4 className={styles.confirmTitle}>Archive {productName}?</h4>
        <p className={styles.confirmMsg}>This will hide the product from all views.</p>
        <div className={styles.confirmActions}>
          <button className={styles.cancelBtn} onClick={onCancel}>Cancel</button>
          <button className={styles.deleteConfirmBtn} onClick={onConfirm}>Archive</button>
        </div>
      </div>
    </div>
  );
}

export default function ProductsSection() {
  const { user }                              = useAuth();
  const [products, setProducts]               = useState([]);
  const [search, setSearch]                   = useState('');
  const [category, setCategory]               = useState('All');
  const [showAdd, setShowAdd]                 = useState(false);
  const [editTarget, setEditTarget]           = useState(null);
  const [deleteTarget, setDeleteTarget]       = useState(null);
  const [toast, setToast]                     = useState(null);
  const dismissToast                          = useCallback(() => setToast(null), []);

  useEffect(() => {
    const q = query(collection(db, 'products'), where('isArchived', '==', false));
    return onSnapshot(q, snap => {
      setProducts(snap.docs.map(d => ({ _docId: d.id, ...d.data() })));
    });
  }, []);

  const filtered = products.filter(p => {
    const matchSearch = !search ||
      p.name?.toLowerCase().includes(search.toLowerCase()) ||
      p._docId.toLowerCase().includes(search.toLowerCase());
    const matchCat = category === 'All' || p.category === category;
    return matchSearch && matchCat;
  });

  async function handleAdd(data) {
    try {
      let imageUrl = null;
      if (data.imageFile) {
        const storageRef = ref(storage, `products/${Date.now()}_${data.imageFile.name}`);
        const snap = await uploadBytes(storageRef, data.imageFile);
        imageUrl = await getDownloadURL(snap.ref);
      }
      await addDoc(collection(db, 'products'), {
        name:         data.name,
        category:     data.category,
        unit:         data.unit ?? '',
        price:        data.price,
        stock:        data.stock,
        description:  data.description ?? '',
        demandWeight: data.demandWeight ?? 2,
        imageUrl:     imageUrl,
        isArchived:   false,
        createdAt:    serverTimestamp(),
      });
      await addAuditLog(user.uid, user.name, `Added product ${data.name}`, 'Maintenance');
      setShowAdd(false);
      setToast({ message: 'Product added.', type: 'success' });
    } catch (e) {
      console.error('Add product failed:', e);
      setToast({ message: 'Failed to add product.', type: 'error' });
    }
  }

  async function handleEdit(data) {
    try {
      let imageUrl = data.imageUrl ?? null;
      if (data.imageFile) {
        const storageRef = ref(storage, `products/${Date.now()}_${data.imageFile.name}`);
        const snap = await uploadBytes(storageRef, data.imageFile);
        imageUrl = await getDownloadURL(snap.ref);
      }
      await updateDoc(doc(db, 'products', data._docId ?? data.id), {
        name:         data.name,
        category:     data.category,
        unit:         data.unit ?? '',
        price:        data.price,
        stock:        data.stock,
        description:  data.description ?? '',
        demandWeight: data.demandWeight ?? 2,
        imageUrl:     imageUrl,
      });
      await addAuditLog(user.uid, user.name, `Updated product ${data.name}`, 'Maintenance');
      setEditTarget(null);
      setToast({ message: 'Product updated.', type: 'success' });
    } catch (e) {
      console.error('Edit product failed:', e);
      setToast({ message: 'Failed to update product.', type: 'error' });
    }
  }

  async function handleDeleteConfirm() {
    try {
      await updateDoc(doc(db, 'products', deleteTarget._docId), { isArchived: true });
      await addAuditLog(user.uid, user.name, `Archived product ${deleteTarget.name}`, 'Maintenance');
      setToast({ message: `${deleteTarget.name} archived.`, type: 'success' });
    } catch (e) {
      console.error('Archive product failed:', e);
      setToast({ message: 'Failed to archive product.', type: 'error' });
    } finally {
      setDeleteTarget(null);
    }
  }

  return (
    <div className={styles.section}>
      <div className={styles.toolbar}>
        <div className={styles.searchWrap}>
          <svg className={styles.searchIcon} viewBox="0 0 20 20" fill="none">
            <circle cx="9" cy="9" r="6" stroke="currentColor" strokeWidth="1.8"/>
            <path d="M13.5 13.5L17 17" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
          </svg>
          <input
            className={styles.searchInput}
            type="text"
            placeholder="Search by name or ID…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>

        <select
          className={styles.catSelect}
          value={category}
          onChange={e => setCategory(e.target.value)}
        >
          {CATEGORIES.map(c => <option key={c} value={c}>{c === 'All' ? 'All Categories' : c}</option>)}
        </select>

        <button className={styles.addBtn} onClick={() => setShowAdd(true)}>+ Add Product</button>
      </div>

      <div className={styles.tableWrap}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Image</th>
              <th>Product ID</th>
              <th>Product Name</th>
              <th>Category</th>
              <th>Unit</th>
              <th>Stock</th>
              <th>Price</th>
              <th>Status</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={9} className={styles.empty}>
                  {products.length === 0 ? 'Loading products…' : 'No products match your filters.'}
                </td>
              </tr>
            ) : (
              filtered.map((p, i) => (
                <tr key={p._docId} className={i % 2 === 0 ? styles.rowEven : styles.rowOdd}>
                  <td><img
                    src={`/${p.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')}.png`}
                    alt={p.name}
                    className={styles.imgPlaceholder}
                    style={{  }}
                  /></td>
                  <td className={styles.productId}>{p._docId}</td>
                  <td className={styles.productName}>{p.name}</td>
                  <td>{p.category}</td>
                  <td>{p.unit ?? '—'}</td>
                  <td className={styles.stockNum}>{p.stock}</td>
                  <td className={styles.price}>₱{p.price?.toLocaleString()}</td>
                  <td><StatusBadge stock={p.stock} /></td>
                  <td>
                    <div className={styles.actionGroup}>
                      <button className={styles.editBtn} onClick={() => setEditTarget(p)}>Edit</button>
                      <button className={styles.deleteBtn} onClick={() => setDeleteTarget(p)}>Archive</button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {showAdd && (
        <ProductModal
          mode="add"
          onSave={handleAdd}
          onCancel={() => setShowAdd(false)}
        />
      )}

      {editTarget && (
        <ProductModal
          mode="edit"
          initialData={{ ...editTarget, id: editTarget._docId }}
          onSave={handleEdit}
          onCancel={() => setEditTarget(null)}
        />
      )}

      {deleteTarget && (
        <ConfirmDialog
          productName={deleteTarget.name}
          onConfirm={handleDeleteConfirm}
          onCancel={() => setDeleteTarget(null)}
        />
      )}

      {toast && (
        <ToastNotification
          message={toast.message}
          type={toast.type}
          onDismiss={dismissToast}
        />
      )}
    </div>
  );
}
